/**
 * Search USDA FoodData Central and hand a chosen food back to the ingredient
 * form that opened this.
 *
 * Shared by the ingredients page and the recipe form's inline create, so the
 * lookup behaves identically in both. It never writes anything: the result only
 * fills the form, and the user still confirms there. That keeps the confirm
 * step, the duplicate-name check and the recipe form's own state in one place.
 */

import { ApiError } from '@/api/client'
import { useFoodLookupStatus, useFoodSearch } from '@/api/hooks'
import type { FoodSearchResult } from '@/api/types'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { EmptyState, ErrorNote, SkeletonRows } from '@/components/ui/Feedback'
import { Note } from '@/components/ui/Note'
import { Dialog } from '@/components/ui/Overlay'
import { cn } from '@/lib/cn'
import { formatMacro } from '@/lib/format'
import { Search } from 'lucide-react'
import { useEffect, useState } from 'react'

/** Long enough to skip the intermediate states of a typed word. */
const DEBOUNCE_MS = 300

/** Must match `DEFAULT_PAGE_SIZE` in the backend service. */
const PAGE_SIZE = 20

/** The last page the backend will serve; matches its `MAX_PAGE`. */
const LAST_PAGE = 10

/** The macros a confirmed result fills in, in form order, with their units. */
const MACROS = [
  ['calories', 'Calories', 'kcal'],
  ['protein', 'Protein', 'g'],
  ['carbs', 'Carbs', 'g'],
  ['fat', 'Fat', 'g'],
  ['fiber', 'Fibre', 'g'],
] as const

export function FoodLookupDialog({
  open,
  query: initialQuery,
  onClose,
  onSelect,
}: {
  open: boolean
  /** Pre-filled with the name already typed in the form. */
  query: string
  onClose: () => void
  onSelect: (food: FoodSearchResult) => void
}) {
  const [term, setTerm] = useState(initialQuery)
  const [debounced, setDebounced] = useState(initialQuery.trim())
  const [page, setPage] = useState(1)
  const [chosen, setChosen] = useState<FoodSearchResult | null>(null)

  const status = useFoodLookupStatus()
  const search = useFoodSearch(debounced, page)

  // Callers mount this component only while it is open, so it re-seeds the
  // search from whatever the form now holds. Deriving that here would mean
  // resetting five pieces of state on every keystroke upstream.

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [term])

  function typing(value: string) {
    setTerm(value)
    setPage(1)
    setChosen(null)
  }

  const enabled = status.data?.enabled ?? false
  const results = search.data?.foods ?? []
  const totalHits = search.data?.total_hits ?? 0

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title="Look up an ingredient"
      description="Nutrition per 100g from USDA FoodData Central."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!chosen}
            onClick={() => {
              if (chosen) onSelect(chosen)
            }}
          >
            Use this food
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {status.isLoading ? <SkeletonRows rows={3} /> : null}

        {status.isError ? (
          <ErrorNote
            title="Could not check whether lookup is available"
            body="You can still enter the macros by hand."
            onRetry={() => void status.refetch()}
          />
        ) : null}

        {status.isSuccess && !enabled ? (
          <Note tone="caution">
            This server has no USDA FoodData Central key configured, so lookup is
            switched off. Add <code className="font-mono">FOODDATACENTRAL_API_KEY</code> to{' '}
            <code className="font-mono">.env</code> and restart to enable it. Entering the
            macros by hand still works.
          </Note>
        ) : null}

        {enabled ? (
          <>
            <div className="relative">
              <Search
                size={15}
                aria-hidden
                className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-faint"
              />
              <input
                type="text"
                aria-label="Search USDA FoodData Central"
                placeholder="Search FoodData Central…"
                value={term}
                data-autofocus
                onChange={(event) => typing(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' || chosen) return
                  event.preventDefault()
                  if (results[0]) setChosen(results[0])
                }}
                className="h-10 w-full rounded-[var(--radius-field)] border border-line-strong bg-surface pr-3 pl-9 text-sm text-ink placeholder:text-ink-faint/70 hover:border-ink-faint focus:border-accent"
              />
            </div>

            {search.isError ? (
              <ErrorNote
                title="Lookup failed"
                body={
                  search.error instanceof ApiError
                    ? search.error.message
                    : 'Could not reach the lookup service.'
                }
                onRetry={() => void search.refetch()}
              />
            ) : null}

            {search.isFetching && !search.data ? <SkeletonRows rows={4} /> : null}

            {search.data && results.length === 0 && !search.isFetching ? (
              <EmptyState
                title="Nothing found"
                body={`FoodData Central has no match for “${search.data.query}”. Try a plainer name, like “chicken breast” rather than “diced chicken breast”.`}
                icon={<Search size={18} />}
              />
            ) : null}

            {results.length > 0 ? (
              <>
                <p className="text-micro text-ink-faint">
                  {totalHits} matches, showing{' '}
                  {page === 1
                    ? `the first ${PAGE_SIZE}`
                    : `results ${(page - 1) * PAGE_SIZE + 1}-${page * PAGE_SIZE}`}
                  .
                </p>
                <ul className="space-y-1.5" role="listbox" aria-label="Search results">
                  {results.map((food) => {
                    const selected = chosen?.fdc_id === food.fdc_id
                    return (
                      <li key={food.fdc_id}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={selected}
                          onClick={() => setChosen(food)}
                          className={cn(
                            'w-full rounded-[var(--radius-field)] border px-3 py-2.5 text-left',
                            selected
                              ? 'border-accent bg-accent-soft'
                              : 'border-line bg-surface hover:border-line-strong',
                          )}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-medium text-ink">
                                {food.description}
                              </span>
                              <span className="block truncate text-micro text-ink-faint">
                                {[food.brand, food.category].filter(Boolean).join(' · ') ||
                                  `FoodData Central #${food.fdc_id}`}
                              </span>
                            </span>
                            {food.data_type ? (
                              <Badge tone="outline" className="shrink-0">
                                {food.data_type}
                              </Badge>
                            ) : null}
                          </div>
                          <Macros food={food} />
                        </button>
                      </li>
                    )
                  })}
                </ul>
                {page < LAST_PAGE && totalHits > page * PAGE_SIZE ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={search.isFetching}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    Show more
                  </Button>
                ) : null}
              </>
            ) : null}
          </>
        ) : null}
      </div>
    </Dialog>
  )
}

/** The five macros per 100 g, or a plain note when the food carries none. */
function Macros({ food }: { food: FoodSearchResult }) {
  if (food.calories === null && food.protein === null) {
    return (
      <p className="mt-1.5 text-micro text-ink-faint">
        No nutrition data for this entry.
      </p>
    )
  }
  return (
    <dl className="tabular mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-micro text-ink-soft">
      {MACROS.map(([key, label, unit]) => (
        <div key={key} className="flex gap-1">
          <dt className="text-ink-faint">{label}</dt>
          <dd>
            {formatMacro(food[key])} <span className="text-ink-faint">{unit}</span>
          </dd>
        </div>
      ))}
    </dl>
  )
}